/**
 * Core Memory Intelligence Client
 * Supports API-first with offline-fallback capability
 */

import {
  MemoryIntelligenceConfig,
  AnalyzePatternsParams,
  PatternAnalysis,
  SuggestTagsParams,
  TagSuggestionsResult,
  FindRelatedParams,
  RelatedMemoriesResult,
  DetectDuplicatesParams,
  DuplicatesResult,
  ExtractInsightsParams,
  InsightsResult,
  HealthCheckParams,
  MemoryHealth,
  QueryMemoriesOptions,
  MemoryEntry,
  ProcessingMode,
  MemoryTypeValue,
  QueryScopeValue,
} from "./types.js";
import {
  PredictiveRecallParams,
  PredictiveRecallResult,
} from "./prediction-types.js";
import {
  RecordBehaviorParams,
  RecordBehaviorResult,
  RecallBehaviorParams,
  RecallBehaviorResult,
  SuggestActionParams,
  SuggestActionResult,
  BehaviorPattern,
} from "./behavior-types.js";
import { DatabaseError } from "./errors.js";
import { HttpClient } from "../utils/http-client.js";
import { UsageInfo, TierInfo } from "../utils/response-adapter.js";

const DEFAULT_API_URL = "https://api.lanonasis.com/api/v1";

type MemoryQueryContextPayload = {
  organizationId?: string;
  topicId?: string;
  memoryTypes?: MemoryTypeValue[];
  queryScope?: QueryScopeValue;
};

function appendQueryContext(
  params: URLSearchParams,
  context: MemoryQueryContextPayload
): void {
  if (context.organizationId) {
    params.append("organization_id", context.organizationId);
  }

  if (context.topicId) {
    params.append("topic_id", context.topicId);
  }

  if (context.queryScope) {
    params.append("query_scope", context.queryScope);
  }

  for (const memoryType of context.memoryTypes || []) {
    params.append("memory_types", memoryType);
  }
}

export interface IntelligenceResponse<T> {
  data: T;
  usage?: UsageInfo;
  tier_info?: TierInfo;
  fromCache?: boolean;
}

export class MemoryIntelligenceClient {
  private httpClient: HttpClient;
  private defaultResponseFormat: "json" | "markdown";
  private processingMode: ProcessingMode;

  constructor(config: MemoryIntelligenceConfig) {
    this.processingMode = config.processingMode || "api";
    const authType = config.authType || (config.apiKey ? "apiKey" : "bearer");

    this.httpClient = new HttpClient({
      apiUrl: config.apiUrl || DEFAULT_API_URL,
      apiKey: config.apiKey,
      authToken: config.authToken,
      authType,
      allowMissingAuth: config.allowMissingAuth,
      timeout: config.timeout,
      headers: config.headers,
      processingMode: this.processingMode,
      enableCache: config.enableCache,
      cacheTTL: config.cacheTTL,
    });

    this.defaultResponseFormat = config.responseFormat || "markdown";
  }

  /**
   * Get the current processing mode
   */
  getProcessingMode(): ProcessingMode {
    return this.processingMode;
  }

  /**
   * Check if cache is enabled (for offline-fallback mode)
   */
  isCacheEnabled(): boolean {
    return this.httpClient.isCacheEnabled();
  }

  /**
   * Clear the response cache
   */
  clearCache(): void {
    this.httpClient.clearCache();
  }

  /**
   * Get HTTP client for direct API access
   */
  public getHttpClient(): HttpClient {
    return this.httpClient;
  }

  /**
   * Query memories from the API
   */
  public async queryMemories(
    userId: string,
    options: QueryMemoriesOptions = {}
  ): Promise<MemoryEntry[]> {
    const {
      type,
      limit = 100,
      offset = 0,
      organizationId,
      topicId,
      memoryTypes,
      queryScope,
    } = options;

    const params = new URLSearchParams({
      user_id: userId,
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (type) {
      params.append("type", type);
    }

    appendQueryContext(params, {
      organizationId,
      topicId,
      memoryTypes,
      queryScope,
    });

    const response = await this.httpClient.getEnhanced<{ memories: MemoryEntry[] }>(
      `/intelligence/memories?${params.toString()}`
    );

    if (response.error) {
      throw new DatabaseError(`Failed to query memories: ${response.error.message}`);
    }

    return response.data?.memories || [];
  }

  /**
   * Analyze usage patterns and trends in memory collection
   */
  async analyzePatterns(params: AnalyzePatternsParams): Promise<IntelligenceResponse<PatternAnalysis>> {
    const response = await this.httpClient.postEnhanced<PatternAnalysis>(
      "/intelligence/analyze-patterns",
      {
        ...params,
        responseFormat: params.responseFormat || this.defaultResponseFormat,
      }
    );

    if (response.error) {
      throw new DatabaseError(`Failed to analyze patterns: ${response.error.message}`);
    }

    return {
      data: response.data!,
      usage: response.usage,
      tier_info: response.tier_info,
      fromCache: response.fromCache,
    };
  }

  /**
   * Get AI-powered tag suggestions for a memory
   */
  async suggestTags(params: SuggestTagsParams): Promise<IntelligenceResponse<TagSuggestionsResult>> {
    const response = await this.httpClient.postEnhanced<TagSuggestionsResult>(
      "/intelligence/suggest-tags",
      {
        ...params,
        responseFormat: params.responseFormat || this.defaultResponseFormat,
      }
    );

    if (response.error) {
      throw new DatabaseError(`Failed to suggest tags: ${response.error.message}`);
    }

    return {
      data: response.data!,
      usage: response.usage,
      tier_info: response.tier_info,
      fromCache: response.fromCache,
    };
  }

  /**
   * Find semantically related memories using vector similarity
   */
  async findRelated(params: FindRelatedParams): Promise<IntelligenceResponse<RelatedMemoriesResult>> {
    const response = await this.httpClient.postEnhanced<RelatedMemoriesResult>(
      "/intelligence/find-related",
      {
        ...params,
        responseFormat: params.responseFormat || this.defaultResponseFormat,
      }
    );

    if (response.error) {
      throw new DatabaseError(`Failed to find related memories: ${response.error.message}`);
    }

    return {
      data: response.data!,
      usage: response.usage,
      tier_info: response.tier_info,
      fromCache: response.fromCache,
    };
  }

  /**
   * Detect potential duplicate memories
   */
  async detectDuplicates(params: DetectDuplicatesParams): Promise<IntelligenceResponse<DuplicatesResult>> {
    const response = await this.httpClient.postEnhanced<DuplicatesResult>(
      "/intelligence/detect-duplicates",
      {
        ...params,
        responseFormat: params.responseFormat || this.defaultResponseFormat,
      }
    );

    if (response.error) {
      throw new DatabaseError(`Failed to detect duplicates: ${response.error.message}`);
    }

    return {
      data: response.data!,
      usage: response.usage,
      tier_info: response.tier_info,
      fromCache: response.fromCache,
    };
  }

  /**
   * Extract insights and patterns from memories
   */
  async extractInsights(params: ExtractInsightsParams): Promise<IntelligenceResponse<InsightsResult>> {
    const response = await this.httpClient.postEnhanced<InsightsResult>(
      "/intelligence/extract-insights",
      {
        ...params,
        responseFormat: params.responseFormat || this.defaultResponseFormat,
      }
    );

    if (response.error) {
      throw new DatabaseError(`Failed to extract insights: ${response.error.message}`);
    }

    return {
      data: response.data!,
      usage: response.usage,
      tier_info: response.tier_info,
      fromCache: response.fromCache,
    };
  }

  /**
   * Check the health and organization quality of memories
   */
  async healthCheck(params: HealthCheckParams): Promise<IntelligenceResponse<MemoryHealth>> {
    const response = await this.httpClient.postEnhanced<MemoryHealth>(
      "/intelligence/health-check",
      {
        ...params,
        responseFormat: params.responseFormat || this.defaultResponseFormat,
      }
    );

    if (response.error) {
      throw new DatabaseError(`Failed to check health: ${response.error.message}`);
    }

    return {
      data: response.data!,
      usage: response.usage,
      tier_info: response.tier_info,
      fromCache: response.fromCache,
    };
  }

  /**
   * Predictive Memory Recall - AI that anticipates what you need
   *
   * Uses a weighted scoring algorithm:
   *   - Semantic similarity to current context (40%)
   *   - Temporal relevance (recency decay curve) (30%)
   *   - Usage frequency (20%)
   *   - Serendipity factor (adjacent discoveries) (10%)
   *
   * @example
   * ```typescript
   * const result = await client.predictiveRecall({
   *   userId: "user-123",
   *   context: {
   *     currentProject: "Building dashboard components",
   *     recentTopics: ["React", "performance", "hooks"],
   *     contextText: "Optimizing render performance for data tables"
   *   },
   *   limit: 5,
   *   minConfidence: 50
   * });
   *
   * for (const prediction of result.data.predictions) {
   *   console.log(`[${prediction.confidence}%] ${prediction.title}`);
   *   console.log(`  Reason: ${prediction.reason}`);
   *   console.log(`  Action: ${prediction.suggestedAction}`);
   * }
   * ```
   */
  async predictiveRecall(
    params: PredictiveRecallParams
  ): Promise<IntelligenceResponse<PredictiveRecallResult>> {
    const response = await this.httpClient.postEnhanced<PredictiveRecallResult>(
      "/intelligence/predictive-recall",
      {
        userId: params.userId,
        organizationId: params.organizationId,
        topicId: params.topicId,
        queryScope: params.queryScope,
        context: params.context,
        limit: params.limit || 5,
        minConfidence: params.minConfidence || 40,
        includeSerendipity: params.includeSerendipity !== false,
        memoryTypes: params.memoryTypes,
        timeWindowDays: params.timeWindowDays || 90,
        responseFormat: params.responseFormat || this.defaultResponseFormat,
      }
    );

    if (response.error) {
      throw new DatabaseError(`Failed to get predictions: ${response.error.message}`);
    }

    return {
      data: response.data!,
      usage: response.usage,
      tier_info: response.tier_info,
      fromCache: response.fromCache,
    };
  }

  /**
   * Record feedback on a prediction (for improving accuracy over time)
   *
   * @example
   * ```typescript
   * await client.recordPredictionFeedback({
   *   memoryId: "mem-123",
   *   userId: "user-123",
   *   useful: true,
   *   action: "clicked"
   * });
   * ```
   */
  async recordPredictionFeedback(params: {
    memoryId: string;
    userId: string;
    useful: boolean;
    action: "clicked" | "saved" | "dismissed" | "ignored";
    dismissReason?: "not_relevant" | "already_know" | "not_now" | "other";
  }): Promise<void> {
    await this.httpClient.post("/intelligence/prediction-feedback", params);
  }

  // ============================================================================
  // Behavioral Learning Methods
  // ============================================================================

  /**
   * Record a successful behavior pattern for future recall
   *
   * The system automatically:
   * - Generates an embedding from the trigger description
   * - Detects and updates duplicates (>95% similarity)
   * - Tracks usage counts for pattern prioritization
   *
   * @example
   * ```typescript
   * const result = await client.recordBehavior({
   *   user_id: "user-123",
   *   trigger: "User wants to refactor a React component to use hooks",
   *   context: {
   *     project: "dashboard-app",
   *     active_files: ["src/components/DataTable.tsx"]
   *   },
   *   actions: [
   *     { tool: "read_file", parameters: { path: "src/components/DataTable.tsx" }, outcome: "success" },
   *     { tool: "analyze_code", parameters: { focus: "class_components" }, outcome: "success" },
   *     { tool: "edit_file", parameters: { path: "src/components/DataTable.tsx" }, outcome: "success" }
   *   ],
   *   final_outcome: "success"
   * });
   *
   * if (result.data.was_duplicate) {
   *   console.log("Updated existing pattern:", result.data.pattern.id);
   * }
   * ```
   */
  async recordBehavior(
    params: RecordBehaviorParams
  ): Promise<IntelligenceResponse<RecordBehaviorResult>> {
    const actions = params.actions.map((action) => ({
      tool: action.tool,
      ...(action.parameters || action.params
        ? { parameters: action.parameters ?? action.params }
        : {}),
      ...(action.outcome ? { outcome: action.outcome } : {}),
      ...(action.timestamp ? { timestamp: action.timestamp } : {}),
      ...(action.duration_ms !== undefined ? { duration_ms: action.duration_ms } : {}),
    }));

    const response = await this.httpClient.postEnhanced<RecordBehaviorResult>(
      "/intelligence/behavior-record",
      {
        user_id: params.user_id,
        trigger: params.trigger,
        context: params.context || {},
        actions,
        final_outcome: params.final_outcome,
        confidence: params.confidence ?? 0.7,
      }
    );

    if (response.error) {
      throw new DatabaseError(`Failed to record behavior: ${response.error.message}`);
    }

    // Handle the edge function response format
    const data = response.data as any;
    return {
      data: {
        pattern: data.data || data,
        was_duplicate: data.message?.includes("duplicate") || false,
        message: data.message || "Behavior pattern recorded successfully",
      },
      usage: response.usage,
      tier_info: response.tier_info,
      fromCache: response.fromCache,
    };
  }

  /**
   * Recall similar behavior patterns based on current context
   *
   * Use this to find workflows that match the user's current task,
   * enabling AI assistants to suggest proven approaches.
   *
   * @example
   * ```typescript
   * const result = await client.recallBehavior({
   *   user_id: "user-123",
   *   context: {
   *     current_task: "Need to optimize database queries for user dashboard"
   *   },
   *   limit: 5,
   *   similarity_threshold: 0.7
   * });
   *
   * for (const match of result.data.patterns) {
   *   console.log(`[${Math.round(match.similarity_score * 100)}%] ${match.pattern.trigger}`);
   *   console.log(`  Actions: ${match.pattern.actions.map(a => a.tool).join(" → ")}`);
   * }
   * ```
   */
  async recallBehavior(
    params: RecallBehaviorParams
  ): Promise<IntelligenceResponse<RecallBehaviorResult>> {
    const response = await this.httpClient.postEnhanced<RecallBehaviorResult>(
      "/intelligence/behavior-recall",
      {
        user_id: params.user_id,
        context: params.context,
        limit: params.limit ?? 5,
        similarity_threshold: params.similarity_threshold ?? 0.7,
      }
    );

    if (response.error) {
      throw new DatabaseError(`Failed to recall behaviors: ${response.error.message}`);
    }

    // Handle the edge function response format
    const data = response.data as any;
    return {
      data: data.data || data,
      usage: response.usage,
      tier_info: response.tier_info,
      fromCache: response.fromCache,
    };
  }

  /**
   * Get AI-powered action suggestions based on learned patterns
   *
   * Analyzes the current state and completed steps to predict
   * what action the user should take next based on similar workflows.
   *
   * @example
   * ```typescript
   * const result = await client.suggestAction({
   *   user_id: "user-123",
   *   current_state: {
   *     task_description: "Implementing user authentication with OAuth",
   *     completed_steps: [
   *       "create src/auth/oauth.ts",
   *       "inspect package.json"
   *     ]
   *   },
   *   max_suggestions: 3
   * });
   *
   * for (const suggestion of result.data.suggestions) {
   *   console.log(`[${Math.round(suggestion.confidence * 100)}%] ${suggestion.action}`);
   *   console.log(`  Tool: ${suggestion.tool}`);
   *   console.log(`  Why: ${suggestion.reasoning}`);
   * }
   * ```
   */
  async suggestAction(
    params: SuggestActionParams
  ): Promise<IntelligenceResponse<SuggestActionResult>> {
    const response = await this.httpClient.postEnhanced<SuggestActionResult>(
      "/intelligence/behavior-suggest",
      {
        user_id: params.user_id,
        current_state: params.current_state,
        max_suggestions: params.max_suggestions ?? 3,
      }
    );

    if (response.error) {
      throw new DatabaseError(`Failed to get action suggestions: ${response.error.message}`);
    }

    // Handle the edge function response format
    const data = response.data as any;
    return {
      data: data.data || data,
      usage: response.usage,
      tier_info: response.tier_info,
      fromCache: response.fromCache,
    };
  }

  /**
   * List all behavior patterns for a user
   *
   * @example
   * ```typescript
   * const patterns = await client.listBehaviorPatterns("user-123", { limit: 20 });
   * console.log(`Found ${patterns.length} patterns`);
   * ```
   */
  async listBehaviorPatterns(
    userId: string,
    options: { limit?: number; offset?: number } = {}
  ): Promise<BehaviorPattern[]> {
    const { limit = 50, offset = 0 } = options;

    const response = await this.httpClient.get<{ patterns: BehaviorPattern[] }>(
      `/intelligence/behavior-patterns?user_id=${userId}&limit=${limit}&offset=${offset}`
    );

    if (response.error) {
      throw new DatabaseError(`Failed to list behavior patterns: ${response.error.message}`);
    }

    return response.data?.patterns || [];
  }
}
